import React, { useState, useMemo } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Box, TableSortLabel, Skeleton, Typography, styled, IconButton,
  TablePagination,
} from "@mui/material";
import ReusableMuiIcons from "../../_helpers/ReusableMuiIcons";
import ReusableButton from "./reusableButtonComponents";
import NoOfferYetCreated from "../../module/barter/noOfferYetCreated";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const StyledTableContainer = styled(TableContainer)(() => ({
  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
  borderRadius: "12px",
  backgroundColor: "#ffffff",
  border: "1px solid #f0f0f0",
}));

const StyledTableHead = styled(TableHead)(() => ({
  backgroundColor: "#F5F6FF",
  "& .MuiTableCell-head": {
    backgroundColor: "#F5F6FF",
    fontWeight: 600,
    fontSize: "0.875rem",
    color: "#7A7A7A",
    borderBottom: "1px solid #e5e7eb",
    padding: "16px 0px 16px 10px",
    "&:last-of-type": {
      paddingRight: "24px",
    },
  },
}));

const StyledTableBody = styled(TableBody)(() => ({
  "& .MuiTableCell-body": {
    color: "#4A525F",
    paddingLeft: "10px",
    paddingRight: "10px"
  },
}));

const StyledCheckbox = styled('input')(({ disabled }) => ({
  width: '25px',
  height: '15px',
  marginLeft: '8px',
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.4 : 1,
}));

const approveBtn = {
  backgroundColor: '#219653 !important',
  border: 'none !important',
};

const Tablecomponent = ({
  loader,
  columns,
  data = [],
  rowsPerPage,
  page,
  count,
  onPageChange,
  rowsPerPageOptions,
  onRowsPerPageChange,
  selectedRows = [],
  setSelectedRows,
  onApproveSelected,
  onRejectSelected,
  onDeleteSelected,
  message,
  content,
  groupBy = null,
  groupExpandable = true,
  defaultExpanded = false,
  bulkActions = ['approve', 'reject', 'delete'],
  onRowClick,
}) => {
  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState("asc");
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  // const isSelectedAll = data.length > 0 && selectedRows.length === data.length;
  const isSelectedAll = selectedRows.length > 0;

  const allRowsDisabled = data.every(
    (row) => row.status === "Approved" || row.status === "Rejected" || row.Status === "approved" || row.Status === 'rejected'
  );

  const hasNonFinalSelection = selectedRows.some((id) =>
    data.some(
      (row) =>
        row.id === id &&
        row.Status !== "approved" &&
        row.Status !== "rejected" 
    )
  );

  const handleApproveSelected = async () => {
    await onApproveSelected(selectedRows);
    setSelectedRows([]);
  };

  const handleRejectSelected = async () => {
    await onRejectSelected(selectedRows);
    setSelectedRows([]);
  };

  const handleSingleApprove = async () => {
    await onApproveSelected(selectedRows[0]);
    setSelectedRows([]);
  };

  const handleSingleReject = async () => {
    await onRejectSelected(selectedRows[0]);
    setSelectedRows([]);
  };

  const handleSort = (property) => () => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (e) => {
    if (e.target.checked) {
      const allIds = data
        .filter((row) => row.status !== "Approved" && row.status !== "Rejected" && row.Status !== "approved" && row.Status !== 'rejected')
        .map((row) => row.id);
      setSelectedRows(allIds);
    } else {
      setSelectedRows([]);
    }
  };

  const handleRowSelect = (rowId) => (e) => {
    const updatedSelection = e.target.checked
      ? [...selectedRows, rowId]
      : selectedRows.filter((id) => id !== rowId);
    setSelectedRows(updatedSelection);
  };

  const toggleGroup = (groupKey) => {
    const newExpanded = new Set(expandedGroups);
    newExpanded.has(groupKey)
      ? newExpanded.delete(groupKey)
      : newExpanded.add(groupKey);
    setExpandedGroups(newExpanded);
  };

  const processedData = useMemo(() => {
    if (!groupBy) {
      return [...data].sort((a, b) => {
        if (!orderBy) return 0;
        const aVal = a[orderBy];
        const bVal = b[orderBy];
        return order === "asc" ? (aVal < bVal ? -1 : 1) : aVal > bVal ? -1 : 1;
      });
    }

    const grouped = data.reduce((acc, item) => {
      const key = item[groupBy];
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {});

    const result = [];
    Object.entries(grouped).forEach(([groupKey, items]) => {
      const sortedItems = items.sort((a, b) => {
        if (!orderBy) return 0;
        const aVal = a[orderBy];
        const bVal = b[orderBy];
        return order === "asc" ? (aVal < bVal ? -1 : 1) : aVal > bVal ? -1 : 1;
      });

      const firstItem = sortedItems[0];
      result.push({
        ...firstItem,
        isMainRow: true,
        groupKey,
        hasChildren: sortedItems.length > 1,
        parentGroup: groupKey,
      });

      if (sortedItems.length > 1 && (expandedGroups.has(groupKey) || defaultExpanded)) {
        sortedItems.slice(1).forEach((item) => {
          result.push({
            ...item,
            isChild: true,
            parentGroup: groupKey,
          });
        });
      }
    });

    return result;
  }, [data, groupBy, orderBy, order, expandedGroups, defaultExpanded]);

  return (
    <Box>
      {loader ? (
        <StyledTableContainer>
          <Table stickyHeader>
            <StyledTableBody>
              <TableRow>
                <TableCell colSpan={columns.length}>
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} animation="wave" height={50} />
                  ))}
                </TableCell>
              </TableRow>
            </StyledTableBody>
          </Table>
        </StyledTableContainer>
      ) : data.length === 0 ? (
        <Box py={4}>
          <NoOfferYetCreated message={message} content={content} />
        </Box>
      ) : (
        <StyledTableContainer>
          <Table stickyHeader>
            <StyledTableHead>
              {selectedRows.length > 0 ? (
                <TableRow>
                  <TableCell>
                    <StyledCheckbox
                      type="checkbox"
                      checked={isSelectedAll}
                      onChange={handleSelectAllClick}
                    />
                  </TableCell>
                  <TableCell colSpan={columns.length - 1}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle2" fontWeight={600}>
                        {selectedRows.length} Selected
                      </Typography>
                      <Box display="flex" gap={1}>
                        {selectedRows.length === 1 ? (
                          <>
                            {bulkActions.includes("approve") && hasNonFinalSelection && (
                              <ReusableButton
                                children="Approve"
                                // onClick={() => onApproveSelected(selectedRows[0])}
                                onClick={handleApproveSelected}
                                sx={approveBtn}
                                classNames="gn-actionbtn"
                              />
                            )}
                            {bulkActions.includes("reject") && hasNonFinalSelection && (
                              <ReusableButton
                                children="Reject"
                                // onClick={() => onRejectSelected(selectedRows[0])}
                                onClick={handleRejectSelected}
                                classNames="gn-actionbtn"
                              />
                            )}
                          </>
                        ) : (
                          <>
                            {bulkActions.includes("approve") && (
                              <ReusableButton
                                children="Approve All"
                                // onClick={onApproveSelected}
                                onClick={handleSingleApprove}
                                sx={approveBtn}
                                classNames="gn-actionbtn"
                              />
                            )}
                            {bulkActions.includes("reject") && (
                              <ReusableButton
                                children="Reject All"
                                // onClick={onRejectSelected}
                                onClick={handleSingleReject}
                                classNames="gn-actionbtn"
                              />
                            )}
                          </>
                        )}
                        {bulkActions.includes("delete") && (
                          <ReusableButton
                            children="Delete Selected"
                            onClick={onDeleteSelected}
                            variant="outlined"
                            classNames="gn-actionbtn-outline gn-actionbtn-color"
                            startIcon={<ReusableMuiIcons name="delete" />}
                          />
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                // default header row
                <TableRow>
                  {columns.map((col, idx) => (
                    <TableCell key={idx}>
                      {col.field === "checkbox" ? (
                        <StyledCheckbox
                          type="checkbox"
                          checked={isSelectedAll}
                          onChange={handleSelectAllClick}
                          disabled={allRowsDisabled}
                        />
                      ) : col.sortable ? (
                        <TableSortLabel
                          active={orderBy === col.field}
                          direction={orderBy === col.field ? order : "asc"}
                          onClick={handleSort(col.field)}
                        >
                          {col.headerName}
                        </TableSortLabel>
                      ) : (
                        col.headerName
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              )}
            </StyledTableHead>
            <StyledTableBody>
              {processedData.map((row, rowIndex) => {
                const isMainRow = row.isMainRow;
                const isChild = row.isChild;
                return (
                  <TableRow
                    key={rowIndex}
                    hover
                    onClick={(e) => {
                      if (
                        e.target.closest("input") ||
                        e.target.closest("button") ||
                        e.target.closest("svg")
                      ) return;
                      if (onRowClick) onRowClick(row);
                    }}
                    sx={{
                      backgroundColor: (() => {
                        let { status,Status, shipping_status, deliverables_status, live_status } = row;

                        if (
                          status === "Approved" &&
                          Status === "approved" &&
                          shipping_status === "Delivered" &&
                          deliverables_status === "Approved" &&
                          live_status === "Approved"
                        ) {
                          return "#EBFFF7";
                        }

                        if (
                          status === "Rejected" ||
                           Status === "rejected" ||
                          shipping_status === "Delivered Failed" ||
                          deliverables_status === "Rejected" ||
                          live_status === "Rejected"
                        ) {
                          return "#FFF2F2";
                        }

                        if (
                          [status, Status, shipping_status, deliverables_status, live_status].some(
                            (val) => val === "Need-changes" || val === "Pending"
                          )
                        ) {
                          return "#F5F6FF";
                        }

                        return "#F5F6FF";
                      })(),
                      cursor: 'pointer'
                    }}
                  >
                    {columns.map((column, colIndex) => {
                      if (column.field === "checkbox") {
                        const status = (row.status || row.Status || "");
                        const isDisabled = status === "Approved" || status === "Rejected" || status === 'approved' || status === 'rejected';
                        return (
                          <TableCell key={colIndex}>
                            <Box display="flex" alignItems="center">
                              <StyledCheckbox
                                type="checkbox"
                                checked={selectedRows.includes(row.id)}
                                onChange={handleRowSelect(row.id)}
                                disabled={isDisabled}
                              />
                              {isMainRow && groupExpandable && row.hasChildren && (
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleGroup(row.groupKey);
                                  }}
                                  sx={{ p: 0.5 }}
                                >
                                  {expandedGroups.has(row.groupKey) ? (
                                    <ExpandMoreIcon fontSize="small" />
                                  ) : (
                                    <ChevronRightIcon fontSize="small" />
                                  )}
                                </IconButton>
                              )}
                            </Box>
                          </TableCell>
                        );
                      }
                      const value = row[column.field];

                      if (isMainRow) {
                        return (
                          <TableCell key={colIndex}>
                            {column.renderCell ? column.renderCell({ row, value }) : value ?? "--"}
                          </TableCell>
                        );
                      }

                      if (isChild && column.field === groupBy) {
                        return <TableCell key={colIndex}></TableCell>;
                      }

                      return (
                        <TableCell key={colIndex}>
                          {column.renderCell ? column.renderCell({ row, value }) : value ?? "--"}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </StyledTableBody>
          </Table>
        </StyledTableContainer>
      )}
      <Box display="flex" justifyContent="flex-end">
        <TablePagination
          component="div"
          count={count}
          page={page}
          onPageChange={onPageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPageOptions={rowsPerPageOptions}
          sx={{
            "& .MuiTablePagination-selectLabel": {
              paddingTop: '14px'
            },
            "& .MuiTablePagination-select": {
              display: "inline-flex",
              alignItems: "center",
              paddingY: "0px",
            },
            "& .MuiTablePagination-displayedRows": {
              margin: 0,
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default Tablecomponent;
